import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Icon } from "iconsax-react";
import { DocumentText1, Import, Edit, User, Calendar, Clock } from "iconsax-react";
import { ModalForm, Button, Prompt, Badge, Skeleton } from "@/shared/ui";
import { DetailFieldRow } from "@/features/hr/shared";
import { SigningStatus } from "@/shared/components";
import { useAuth } from "@/shared/hooks";
import { formatDateForDisplay, type Locale } from "@/shared/utils";
import { getOrderActions } from "@/features/hr/contracts/utils";
import { useSignOrderOnHiringMutation, usePreviewOrderOnHiringMutation, useGetOrderOnHiringQuery } from "@/features/hr/contracts/api";
import { useOrderSigning } from "@/features/signing";
import type { OrderOnHiringNested } from "@/features/hr/contracts/types";

interface Props {
  candidateName: string;
  orderId: number;
  orderOnHiring?: OrderOnHiringNested | null;
  originalFileUrlOverride?: string | null;
  onClose: () => void;
  onPreview?: () => Promise<void>;
  isPreviewing?: boolean;
  onUploaded?: () => void;
  locale?: Locale;
}

export default function OrderDetailsModal({
  candidateName,
  orderId,
  orderOnHiring: orderOnHiringProp,
  originalFileUrlOverride,
  onClose,
  onPreview,
  isPreviewing = false,
  onUploaded,
  locale = "ru",
}: Props) {
  const { t } = useTranslation("ContractsPage");
  const { t: tCommon } = useTranslation("Common");
  const { hasRole } = useAuth();

  const shouldFetch = !orderOnHiringProp && orderId > 0;
  const { data: fetchedOrder, isLoading: isFetchingOrder } = useGetOrderOnHiringQuery(orderId, { skip: !shouldFetch });
  const orderOnHiring = orderOnHiringProp ?? fetchedOrder ?? null;

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [previewOrder] = usePreviewOrderOnHiringMutation();
  const [signOrder] = useSignOrderOnHiringMutation();

  const orderSigning = useOrderSigning({
    previewFn() {
      return previewOrder(orderId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      return signOrder({ orderId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: tCommon("signing.success.title"),
        text: tCommon("signing.success.text"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const signingStatus = orderOnHiring?.signing_status ?? null;
  const actionsConfig = getOrderActions(signingStatus);
  const pdfUrl = orderOnHiring?.signed_pdf_url ?? null;
  const originalFileUrl = orderOnHiring?.original_file_url ?? originalFileUrlOverride ?? null;

  interface FieldItem {
    key: string;
    label: string;
    value: string;
    icon: Icon;
    isStatus?: boolean;
    statusComponent?: ReactNode;
    isPdfFile?: boolean;
  }

  const fields: FieldItem[] = [
    {
      key: "candidateName",
      label: t("order.candidateName"),
      value: candidateName || "",
      icon: User,
    },
    {
      key: "createdAt",
      label: t("order.createdAt"),
      value: orderOnHiring?.created_at ? formatDateForDisplay(orderOnHiring.created_at, false) : "",
      icon: Calendar,
    },
    {
      key: "status",
      label: t("order.statusLabel"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent:
        signingStatus === null || signingStatus === 0 ? (
          <Badge
            variant="soft"
            color="notice"
            text={t("order.statusAwaitingSigning")}
            icon={<Clock size={14} color="currentColor" />}
          />
        ) : (
          <SigningStatus signingStatus={signingStatus} locale={locale} />
        ),
    },
    ...((originalFileUrl || pdfUrl)
      ? [
          {
            key: "orderFile",
            label: t("order.orderFileLabel"),
            value: "",
            icon: DocumentText1 as Icon,
            isPdfFile: true,
          },
        ]
      : []),
  ];

  const visibleFields = fields.filter((f) => f.value || f.isStatus || f.isPdfFile);

  function renderField(field: FieldItem, index: number, array: FieldItem[]) {
    const pdfPrimaryUrl = field.isPdfFile ? ((pdfUrl || originalFileUrl) as string) : undefined;
    const pdfSecondaryUrl = field.isPdfFile && pdfUrl && originalFileUrl ? originalFileUrl : undefined;
    return (
      <DetailFieldRow
        key={field.key}
        label={field.label}
        value={field.value}
        icon={field.icon}
        isStatus={field.isStatus}
        statusComponent={field.statusComponent}
        isPdfFile={field.isPdfFile}
        pdfPrimaryUrl={pdfPrimaryUrl}
        pdfSecondaryUrl={pdfSecondaryUrl}
        isLast={index === array.length - 1}
      />
    );
  }

  return (
    <>
      {orderSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${orderSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => {
            const wasSuccess = prompt.variant === "success";
            setPrompt(null);
            if (wasSuccess) onUploaded?.();
          }}
          namespace="ContractsPage"
        />
      )}

      <ModalForm
        icon={DocumentText1}
        onClose={onClose}
        resize={false}
        width="w-xl"
        footer={
          (actionsConfig.showSignButton || actionsConfig.showDownloadButton) &&
          !(shouldFetch && isFetchingOrder) ? (
            <div className="flex flex-col gap-3">
              {actionsConfig.showSignButton && hasRole("chief") && (
                <Button
                  variant="primary"
                  size="huge"
                  className="w-full"
                  disabled={orderSigning.isLoading}
                  onClick={() => orderSigning.handleSign()}>
                  <Edit size={16} color="currentColor" />
                  {tCommon("signing.signOrder")}
                </Button>
              )}
              {actionsConfig.showDownloadButton && onPreview && (
                <Button
                  variant="secondary"
                  size="huge"
                  className="w-full"
                  disabled={isPreviewing || orderSigning.isLoading}
                  onClick={() => onPreview()}>
                  <Import size={16} color="currentColor" />
                  {isPreviewing ? t("order.actions.downloading") : t("order.actions.download")}
                </Button>
              )}
            </div>
          ) : undefined
        }>
        {shouldFetch && isFetchingOrder ? (
          <div className="flex flex-col gap-6 h-full min-h-0">
            <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
              <Skeleton height={28} width={240} />
            </div>
            <div className="flex-1">
              <div className="flex flex-col py-4 gap-3">
                {Array.from({ length: 3 }).map(function (_, idx) {
                  return (
                    <div key={idx} className="py-3 border-b surface-base-stroke">
                      <Skeleton height={20} width="100%" />
                    </div>
                  );
                })}
                <Skeleton height={48} width="100%" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 h-full min-h-0">
            <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
              <h4 className="text-display-2xs content-base-primary">{t("order.title")}</h4>
            </div>

            <div className="flex-1 overflow-auto min-h-0 p-1">
              <div className="flex flex-col py-4">
                <div className="flex flex-col">{visibleFields.map(renderField)}</div>
              </div>
            </div>
          </div>
        )}
      </ModalForm>
    </>
  );
}
